import { api, uploadZip, Prototype, VersionInfo, CreateVersionResponse, getApiUrl } from "./api";
import { currentEmail, signOut } from "./auth";

const VIEWER_BASE = "https://p.ai.rewind.engineering";
const SKELETON_CARDS = 3;
let filterMode: "all" | "mine" | string = "all"; // "all", "mine", or an email address
let allPrototypes: Prototype[] = [];
let maxRetainedVersions: number | undefined;

function skeletonListHtml(): string {
  const card = `
    <div class="skeleton-card" aria-hidden="true">
      <div class="skeleton-line skeleton-title"></div>
      <div class="skeleton-line skeleton-url"></div>
      <div class="skeleton-line skeleton-meta"></div>
    </div>
  `;
  return `
    <div class="skeleton-wrap" role="status" aria-label="Loading prototypes">
      ${card.repeat(SKELETON_CARDS)}
    </div>
  `;
}

// Material Design Icons paths (24x24 viewBox), MIT licensed.
// Inlined to avoid an icon-font dependency.
const HELP_ICON_PATH =
  "M11,18H13V16H11V18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,6A4,4 0 0,0 8,10H10A2,2 0 0,1 12,8A2,2 0 0,1 14,10C14,12 11,11.75 11,15H13C13,12.75 16,12.5 16,10A4,4 0 0,0 12,6Z";
const LOGOUT_ICON_PATH =
  "M14.08,15.59L16.67,13H7V11H16.67L14.08,8.41L15.5,7L20.5,12L15.5,17L14.08,15.59M19,3A2,2 0 0,1 21,5V9.67L19,7.67V5H5V19H19V16.33L21,14.33V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.9 3.89,3 5,3H19Z";

function iconSvg(pathData: string, size = 18): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${pathData}"/></svg>`;
}

export function renderWho() {
  const el = document.getElementById("who");
  if (!el) return;

  const span = document.createElement("span");
  span.className = "who-email";
  span.textContent = `Signed in as ${currentEmail() ?? ""}`;

  // Help: anchor styled as an icon-only button. Anchor (not button) preserves
  // middle-click-to-open-in-new-tab and shows the URL in the status bar — both
  // useful affordances for nav.
  const helpLink = document.createElement("a");
  helpLink.href = "#help";
  helpLink.className = "icon-btn help-btn";
  helpLink.title = "Help";
  helpLink.setAttribute("aria-label", "Help");
  helpLink.innerHTML = iconSvg(HELP_ICON_PATH);

  // Sign out: real button (action, not navigation).
  const signOutBtn = document.createElement("button");
  signOutBtn.type = "button";
  signOutBtn.className = "btn-secondary signout-btn";
  signOutBtn.innerHTML = `${iconSvg(LOGOUT_ICON_PATH, 16)}<span>Sign out</span>`;
  signOutBtn.addEventListener("click", () => {
    signOut();
    window.location.reload();
  });

  el.replaceChildren(span, helpLink, signOutBtn);
}

export async function renderApp(
  root: HTMLElement,
  opts?: {
    maxRetainedVersions?: number;
    initialListPromise?: Promise<{ prototypes: Prototype[] }>;
  },
) {
  maxRetainedVersions = opts?.maxRetainedVersions;
  root.innerHTML = `
    <section class="intro">
      <p>Upload and share Claude-generated HTML prototypes with your team.</p>
      <p>Upload a <strong>.zip file</strong> containing everything needed for your prototype (HTML, CSS, JS, images).
        Include an <code>index.html</code> at the root, or a single <code>.html</code> file that will be used as the entry point.</p>
      <p class="intro-note">Prototype viewer links are <strong>publicly accessible</strong> to anyone with the URL — no sign-in required to view.</p>
    </section>
    <section class="card">
      <h2>Upload</h2>
      <div class="upload-fields">
        <input type="text" id="title" placeholder="Title" />
        <input type="text" id="description" placeholder="Brief description (optional)" maxlength="200" />
        <div class="upload-row">
          <input type="file" id="file" accept=".zip,application/zip" hidden />
          <button id="chooseFileBtn" class="btn-secondary" type="button">Choose .zip file</button>
          <span id="fileName" class="file-name">No file chosen</span>
          <button id="uploadBtn" class="btn-primary">Upload</button>
        </div>
      </div>
      <div id="uploadStatus"></div>
    </section>
    <section class="card">
      <div class="list-header">
        <h2>Prototypes</h2>
        <div class="list-controls">
          <div class="filter-toggle">
            <button id="filterAll" class="filter-btn active">All</button>
            <button id="filterMine" class="filter-btn">Mine</button>
          </div>
          <select id="userFilter" class="user-filter" style="display:none">
            <option value="">All users</option>
          </select>
          <button id="refreshBtn" class="refresh-btn" title="Refresh list">&#8635;</button>
        </div>
      </div>
      <div id="list">${skeletonListHtml()}</div>
    </section>
  `;
  document.getElementById("uploadBtn")!.addEventListener("click", onUpload);
  document.getElementById("chooseFileBtn")!.addEventListener("click", () => {
    document.getElementById("file")!.click();
  });
  document.getElementById("file")!.addEventListener("change", () => {
    const file = (document.getElementById("file") as HTMLInputElement).files?.[0];
    document.getElementById("fileName")!.textContent = file ? file.name : "No file chosen";
  });
  document.getElementById("filterAll")!.addEventListener("click", () => setFilter("all"));
  document.getElementById("filterMine")!.addEventListener("click", () => setFilter("mine"));
  document.getElementById("userFilter")!.addEventListener("change", onUserFilterChange);
  document.getElementById("refreshBtn")!.addEventListener("click", () => refreshList());
  await refreshList(opts?.initialListPromise);
}

function setFilter(mode: "all" | "mine") {
  filterMode = mode;
  document.getElementById("filterAll")!.classList.toggle("active", mode === "all");
  document.getElementById("filterMine")!.classList.toggle("active", mode === "mine");
  const userSelect = document.getElementById("userFilter") as HTMLSelectElement;
  if (mode === "all") {
    userSelect.style.display = "";
    userSelect.value = "";
  } else {
    userSelect.style.display = "none";
  }
  renderFilteredList();
}

function onUserFilterChange() {
  const userSelect = document.getElementById("userFilter") as HTMLSelectElement;
  filterMode = userSelect.value || "all";
  renderFilteredList();
}

function populateUserFilter(prototypes: Prototype[]) {
  const select = document.getElementById("userFilter") as HTMLSelectElement;
  const owners = [...new Set(prototypes.map((p) => p.owner))].sort();
  const current = select.value;
  select.innerHTML = `<option value="">All users</option>`;
  for (const owner of owners) {
    const opt = document.createElement("option");
    opt.value = owner;
    opt.textContent = owner;
    select.appendChild(opt);
  }
  select.value = current;
  select.style.display = filterMode === "mine" ? "none" : "";
}

function getFilteredPrototypes(): Prototype[] {
  if (filterMode === "mine") {
    return allPrototypes.filter((p) => p.owner === currentEmail());
  }
  if (filterMode !== "all" && filterMode !== "") {
    return allPrototypes.filter((p) => p.owner === filterMode);
  }
  return allPrototypes;
}

async function onUpload() {
  const title = (document.getElementById("title") as HTMLInputElement).value.trim();
  const description = (document.getElementById("description") as HTMLInputElement).value.trim();
  const file = (document.getElementById("file") as HTMLInputElement).files?.[0];
  const status = document.getElementById("uploadStatus")!;
  if (!title) { status.textContent = "Title required"; return; }
  if (!file) { status.textContent = "Pick a .zip file"; return; }

  status.textContent = "Creating…";
  try {
    const presigned = await api.create(title, description || undefined);
    status.textContent = "Uploading…";
    await uploadZip(file, presigned);
    status.textContent = "Extracting…";
    const final = await pollUntilReady(presigned.slug);
    if (final.status === "ready") {
      const url = `${VIEWER_BASE}/${presigned.slug}/`;
      status.innerHTML = `Ready: <a href="${url}" target="_blank">${url}</a>`;
    } else {
      status.innerHTML = `<span class="error">${escape(final.error ?? "Failed")}</span>`;
    }
    (document.getElementById("title") as HTMLInputElement).value = "";
    (document.getElementById("description") as HTMLInputElement).value = "";
    (document.getElementById("file") as HTMLInputElement).value = "";
    await refreshList();
  } catch (e) {
    status.innerHTML = `<span class="error">${escape((e as Error).message)}</span>`;
  }
}

async function pollUntilReady(slug: string, version = 1): Promise<VersionInfo> {
  for (let i = 0; i < 60; i++) {
    const v = await api.getVersion(slug, version);
    if (v.status !== "pending") return v;
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error("Timed out waiting for extraction");
}

async function refreshList(preloaded?: Promise<{ prototypes: Prototype[] }>) {
  const el = document.getElementById("list")!;
  // Show the skeleton for manually-triggered refreshes. For the initial
  // render, renderApp already seeded the skeleton in the DOM so we leave it.
  if (!preloaded) el.innerHTML = skeletonListHtml();
  try {
    const { prototypes } = await (preloaded ?? api.list());
    allPrototypes = prototypes;
    populateUserFilter(prototypes);
    renderFilteredList();
  } catch (e) {
    el.innerHTML = `<div class="error">${escape((e as Error).message)}</div>`;
  }
}

function renderFilteredList() {
  const el = document.getElementById("list")!;
  const filtered = getFilteredPrototypes();
  if (filtered.length === 0) {
    const msg = filterMode === "mine"
      ? "You haven't uploaded any prototypes yet."
      : allPrototypes.length === 0
        ? "No prototypes yet."
        : "No prototypes match this filter.";
    el.innerHTML = `<p>${msg}</p>`;
    return;
  }
  el.innerHTML = filtered.map(renderCard).join("");
  for (const p of filtered) {
    document.getElementById(`del-${p.slug}`)?.addEventListener("click", () => onDelete(p.slug));
    document.getElementById(`keep-${p.slug}`)?.addEventListener("click", () => onKeep(p.slug));
    document.getElementById(`unkeep-${p.slug}`)?.addEventListener("click", () => onUnkeep(p.slug));
    // Version toggle
    document.getElementById(`versions-toggle-${p.slug}`)?.addEventListener("click", () => onToggleVersions(p));
    // Upload new version (owner only)
    if (p.owner === currentEmail()) {
      document.getElementById(`version-choose-${p.slug}`)?.addEventListener("click", () => {
        document.getElementById(`version-file-${p.slug}`)!.click();
      });
      document.getElementById(`version-file-${p.slug}`)?.addEventListener("change", () => {
        const file = (document.getElementById(`version-file-${p.slug}`) as HTMLInputElement).files?.[0];
        document.getElementById(`version-filename-${p.slug}`)!.textContent = file ? file.name : "No file chosen";
      });
      document.getElementById(`version-upload-btn-${p.slug}`)?.addEventListener("click", () => onUploadVersion(p.slug));
      document.getElementById(`gen-token-${p.slug}`)?.addEventListener("click", () => onGenerateToken(p.slug));
    }
  }
  for (const btn of el.querySelectorAll<HTMLButtonElement>(".copy-btn")) {
    btn.addEventListener("click", () => {
      navigator.clipboard.writeText(btn.dataset.url ?? "");
      btn.textContent = "\u2713";
      setTimeout(() => { btn.textContent = "\uD83D\uDCCB"; }, 1500);
    });
  }
}

function expiryLabel(p: Prototype): string {
  if (p.kept) return `<span class="expiry-kept">Kept permanently</span>`;
  if (!p.expiresAt) return "";
  const daysLeft = Math.ceil((p.expiresAt * 1000 - Date.now()) / 86400000);
  if (daysLeft <= 0) return `<span class="expiry-soon">Expiring soon</span>`;
  if (daysLeft <= 7) return `<span class="expiry-soon">Expires in ${daysLeft}d</span>`;
  return `<span class="expiry-normal">Expires in ${daysLeft}d</span>`;
}

function renderCard(p: Prototype): string {
  const isMine = p.owner === currentEmail();
  const url = `${VIEWER_BASE}/${p.slug}/`;
  const desc = p.description ? `<div class="proto-desc">${escape(p.description)}</div>` : "";
  const expiry = expiryLabel(p);
  const keepBtn = isMine
    ? (!p.kept
        ? `<button id="keep-${p.slug}" class="keep-btn" title="Keep this prototype permanently">Keep</button>`
        : `<button id="unkeep-${p.slug}" class="unkeep-btn" title="Restore 90-day expiry">Expire</button>`)
    : "";
  return `
    <div class="prototype">
      <div class="proto-info">
        <div class="proto-title"><a href="${url}" target="_blank">${escape(p.title)}</a> <span class="version-badge">v${p.currentVersion}</span></div>
        ${desc}
        <div class="proto-url">
          <a href="${url}" target="_blank">${escape(url)}</a>
          <button class="copy-btn" data-url="${escape(url)}" title="Copy URL">&#128203;</button>
        </div>
        <div class="proto-meta">
          <span>Uploaded by: <strong>${escape(p.owner)}</strong></span>
          <span>Uploaded on: <strong>${escape(new Date(p.uploadedAt).toLocaleString())}</strong></span>
          ${expiry ? `<span>${expiry}</span>` : ""}
        </div>
      </div>
      <div class="proto-actions">
        ${keepBtn}
        ${isMine ? `<button id="del-${p.slug}" class="del-btn">Delete</button>` : ""}
      </div>
      <div class="proto-expandable">
        <button class="versions-toggle" id="versions-toggle-${p.slug}" data-slug="${p.slug}">&#9654; ${p.versionCount} version${p.versionCount === 1 ? "" : "s"}</button>
        <div class="versions-list" id="versions-${p.slug}" style="display:none"></div>
        ${maxRetainedVersions ? `<div class="versions-note" id="versions-note-${p.slug}" style="display:none">Only files for the last ${maxRetainedVersions} version${maxRetainedVersions === 1 ? "" : "s"} remain viewable; older versions may appear with Files removed.</div>` : ""}
        ${isMine ? `
          <div class="version-upload" id="version-upload-${p.slug}" style="display:none">
            <input type="file" id="version-file-${p.slug}" accept=".zip,application/zip" hidden />
            <button class="btn-secondary btn-sm" id="version-choose-${p.slug}">Choose .zip</button>
            <span class="file-name" id="version-filename-${p.slug}">No file chosen</span>
            <input type="text" id="version-note-${p.slug}" placeholder="Change note (optional)" class="version-note-input" />
            <button class="btn-primary btn-sm" id="version-upload-btn-${p.slug}">Upload</button>
            <span id="version-status-${p.slug}"></span>
          </div>
          <div class="token-section" id="token-section-${p.slug}" style="display:none">
            <button class="btn-secondary btn-sm" id="gen-token-${p.slug}">Generate Upload Token</button>
            <div id="token-display-${p.slug}"></div>
          </div>
        ` : ""}
      </div>
    </div>
  `;
}

async function onKeep(slug: string) {
  try {
    await api.keep(slug);
    await refreshList();
  } catch (e) {
    alert((e as Error).message);
  }
}

async function onUnkeep(slug: string) {
  try {
    await api.unkeep(slug);
    await refreshList();
  } catch (e) {
    alert((e as Error).message);
  }
}

async function onDelete(slug: string) {
  if (!confirm("Delete this prototype? The URL will stop working.")) return;
  try {
    await api.del(slug);
    await refreshList();
  } catch (e) {
    alert((e as Error).message);
  }
}

async function onToggleVersions(p: Prototype) {
  const listEl = document.getElementById(`versions-${p.slug}`)!;
  const toggleBtn = document.getElementById(`versions-toggle-${p.slug}`)!;
  const uploadEl = document.getElementById(`version-upload-${p.slug}`);
  const tokenEl = document.getElementById(`token-section-${p.slug}`);
  const noteEl = document.getElementById(`versions-note-${p.slug}`);

  if (listEl.style.display === "none") {
    toggleBtn.innerHTML = `&#9660; ${p.versionCount} version${p.versionCount === 1 ? "" : "s"}`;
    listEl.style.display = "";
    if (uploadEl) uploadEl.style.display = "";
    if (tokenEl) tokenEl.style.display = "";
    if (noteEl) noteEl.style.display = "";
    try {
      const { versions } = await api.listVersions(p.slug);
      const isMine = p.owner === currentEmail();
      // Cap initial render at retained-cap + 5. Pruned ("Files removed") rows
      // beyond that are little more than memory jogs — the explanatory note above
      // already covers their existence — so we tuck them behind an expander to
      // keep prolific prototypes (50+ versions) from rendering as a giant scroll.
      const cap = maxRetainedVersions ? maxRetainedVersions + 5 : versions.length;
      const initial = versions.slice(0, cap);
      const overflow = versions.slice(cap);
      const overflowBtn = overflow.length
        ? `<button class="versions-show-older" data-slug="${p.slug}">Show ${overflow.length} older version${overflow.length === 1 ? "" : "s"}</button>`
        : "";
      listEl.innerHTML = initial.map((v) => renderVersionRow(p, v, isMine)).join("") + overflowBtn;
      // Wire up delete buttons
      for (const btn of listEl.querySelectorAll<HTMLButtonElement>(".version-del-btn")) {
        btn.addEventListener("click", () => onDeleteVersion(btn.dataset.slug!, parseInt(btn.dataset.version!, 10)));
      }
      // Wire up "Show N older" expander — render the overflow rows in a detached
      // fragment first so we can wire their delete buttons before they enter the
      // DOM, then replace the button with the new rows.
      const showOlderBtn = listEl.querySelector<HTMLButtonElement>(".versions-show-older");
      if (showOlderBtn) {
        showOlderBtn.addEventListener("click", () => {
          const tmpl = document.createElement("div");
          tmpl.innerHTML = overflow.map((v) => renderVersionRow(p, v, isMine)).join("");
          for (const btn of tmpl.querySelectorAll<HTMLButtonElement>(".version-del-btn")) {
            btn.addEventListener("click", () => onDeleteVersion(btn.dataset.slug!, parseInt(btn.dataset.version!, 10)));
          }
          showOlderBtn.replaceWith(...Array.from(tmpl.children));
        });
      }
    } catch (e) {
      listEl.innerHTML = `<div class="error">${escape((e as Error).message)}</div>`;
    }
  } else {
    toggleBtn.innerHTML = `&#9654; ${p.versionCount} version${p.versionCount === 1 ? "" : "s"}`;
    listEl.style.display = "none";
    if (uploadEl) uploadEl.style.display = "none";
    if (tokenEl) tokenEl.style.display = "none";
    if (noteEl) noteEl.style.display = "none";
  }
}

function renderVersionRow(p: Prototype, v: VersionInfo, isMine: boolean): string {
  const isCurrent = v.version === p.currentVersion;
  const isDeleted = v.status === "deleted";
  const isFailed = v.status === "failed";
  const viewUrl = `${VIEWER_BASE}/${p.slug}/v${v.version}/`;

  // Left-side badge column
  const badge = isCurrent
    ? `<span class="version-badge-col"><span class="version-current-badge">Current</span></span>`
    : `<span class="version-badge-col"></span>`;

  // Right-side action
  const isPending = v.status === "pending";
  let action: string;
  if (isDeleted) {
    action = `<span class="version-deleted-label">Deleted</span>`;
  } else if (isFailed) {
    action = `<span class="version-failed-label" title="${escape(v.error ?? "Extraction failed")}">Failed <span class="version-info-icon">&#9432;</span></span>`;
  } else if (isPending) {
    action = `<span class="version-pending-label">Pending</span>`;
  } else if (!v.filesRetained) {
    action = `<span class="version-pruned">Files removed</span>`;
  } else {
    action = `<a href="${viewUrl}" target="_blank" rel="noopener noreferrer">View</a>`;
  }

  // Delete button: owner only, not current, not already deleted.
  // Render a placeholder on rows without the button to keep "View" aligned.
  const canDelete = isMine && !isCurrent && !isDeleted;
  const deleteBtn = canDelete
    ? ` <button class="version-del-btn" data-slug="${p.slug}" data-version="${v.version}" title="Delete this version">&#10005;</button>`
    : isMine ? ` <span class="version-del-placeholder"></span>` : "";

  const note = v.note && !isDeleted ? `<span class="version-note">${escape(v.note)}</span>` : "";
  const relative = relativeTime(new Date(v.uploadedAt));
  const meta = isDeleted ? "" : `${escape(v.uploadedBy)} &middot; ${relative}`;

  let rowClass = "version-row";
  if (isCurrent) rowClass += " version-current";
  else if (isDeleted) rowClass += " version-deleted";
  else if (isFailed) rowClass += " version-failed";

  return `
    <div class="${rowClass}">
      ${badge}
      <span class="version-num">v${v.version}</span>
      <span class="version-meta">${meta}</span>
      ${note}
      <span class="version-link">${action}${deleteBtn}</span>
    </div>
  `;
}

function relativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

async function onUploadVersion(slug: string) {
  const file = (document.getElementById(`version-file-${slug}`) as HTMLInputElement).files?.[0];
  const note = (document.getElementById(`version-note-${slug}`) as HTMLInputElement).value.trim();
  const status = document.getElementById(`version-status-${slug}`)!;

  if (!file) { status.textContent = "Pick a .zip file"; return; }

  status.textContent = "Creating version…";
  try {
    const presigned = await api.createVersion(slug, note || undefined);
    status.textContent = "Uploading…";
    const form = new FormData();
    for (const [k, v] of Object.entries(presigned.fields)) form.append(k, v);
    form.append("file", file);
    const res = await fetch(presigned.uploadUrl, { method: "POST", body: form });
    if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
    status.textContent = "Extracting…";
    const final = await pollUntilReady(slug, presigned.version);
    if (final.status === "ready") {
      status.innerHTML = `<span class="success">v${presigned.version} ready</span>`;
    } else {
      status.innerHTML = `<span class="error">${escape(final.error ?? "Failed")}</span>`;
    }
    (document.getElementById(`version-file-${slug}`) as HTMLInputElement).value = "";
    (document.getElementById(`version-note-${slug}`) as HTMLInputElement).value = "";
    document.getElementById(`version-filename-${slug}`)!.textContent = "No file chosen";
    await refreshList();
  } catch (e) {
    status.innerHTML = `<span class="error">${escape((e as Error).message)}</span>`;
  }
}

async function onDeleteVersion(slug: string, version: number) {
  if (!confirm(`Delete version ${version}? This cannot be undone.`)) return;
  try {
    await api.deleteVersion(slug, version);
    await refreshList();
  } catch (e) {
    alert((e as Error).message);
  }
}

async function onGenerateToken(slug: string) {
  const display = document.getElementById(`token-display-${slug}`)!;
  try {
    const { token, expiresAt } = await api.generateToken(slug);
    const expiresIn = Math.floor((expiresAt - Date.now() / 1000) / 3600);
    const apiEndpoint = getApiUrl();

    display.innerHTML = `
      <div class="token-info">
        <div class="token-value">
          <code id="token-code-${slug}">${escape(token)}</code>
          <button class="copy-btn" id="copy-token-${slug}" title="Copy token">&#128203;</button>
        </div>
        <div class="token-expiry">Expires in ~${expiresIn}h</div>
        <details class="token-usage">
          <summary>Usage instructions</summary>
          <pre>
# 1. Get presigned upload URL
curl -X POST \\
  -H "Authorization: ProtoToken ${escape(token)}" \\
  -H "Content-Type: application/json" \\
  -d '{"note": "your change note"}' \\
  ${escape(apiEndpoint)}/prototypes/${escape(slug)}/versions

# 2. Upload ZIP to the returned URL
curl -X POST &lt;uploadUrl&gt; -F "key=..." -F "file=@prototype.zip"
          </pre>
        </details>
        <button class="btn-secondary btn-sm" id="regen-token-${slug}">Regenerate</button>
      </div>
    `;
    document.getElementById(`copy-token-${slug}`)?.addEventListener("click", () => {
      navigator.clipboard.writeText(token);
      document.getElementById(`copy-token-${slug}`)!.textContent = "\u2713";
      setTimeout(() => { document.getElementById(`copy-token-${slug}`)!.textContent = "\uD83D\uDCCB"; }, 1500);
    });
    document.getElementById(`regen-token-${slug}`)?.addEventListener("click", () => onGenerateToken(slug));
  } catch (e) {
    display.innerHTML = `<span class="error">${escape((e as Error).message)}</span>`;
  }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === "&" ? "&amp;" :
    c === "<" ? "&lt;" :
    c === ">" ? "&gt;" :
    c === '"' ? "&quot;" : "&#39;"
  ));
}
